// src/components/FindItemModal.jsx
import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import {
  normalizeProductMasterDoc,
  PRODUCT_MASTER_COLLECTION,
} from "../config/productMasterFirestore";

/** Max table rows to render — keeps DOM light for large catalogs (search narrows the list). */
const MAX_RENDERED_ROWS = 250;

/** Generic FK → display name lookup builder.
 *  - `nameFields`: ordered list of field names to read the display text from
 *    (e.g., ['gname', 'name'] for groups, ['brandname', 'name'] for brands).
 *  - `idFields`: extra numeric/legacy id fields on the master doc to key on
 *    (the doc id is always indexed).
 *  - `keyAllNumericFields`: when true, also key on every numeric field on the
 *    doc. Used for `units` because imported docs store the legacy id under a
 *    variety of names (`unitid`, `unitnameid`, `unitcode`, `code`...) and we
 *    want them all to resolve to the unit's display name.
 */
function buildLookup(records, nameFields, idFields, { keyAllNumericFields = false } = {}) {
  const m = new Map();
  for (const r of records) {
    let name = "";
    for (const f of nameFields) {
      const v = r?.[f];
      if (v != null && String(v).trim() !== "") {
        name = String(v).trim();
        break;
      }
    }
    if (!name) continue;
    const keys = new Set();
    if (r.id != null && String(r.id).trim() !== "") keys.add(String(r.id));
    for (const f of idFields) {
      const v = r?.[f];
      if (v != null && String(v).trim() !== "") keys.add(String(v));
    }
    if (keyAllNumericFields) {
      for (const [k, v] of Object.entries(r)) {
        if (k === "id") continue;
        if (typeof v === "number" && Number.isFinite(v)) {
          keys.add(String(v));
        } else if (typeof v === "string" && /^-?\d+$/.test(v.trim())) {
          keys.add(v.trim());
        }
      }
    }
    for (const k of keys) m.set(k, name);
  }
  return m;
}

/**
 * Items in this collection come in two shapes: the friendly schema written by
 * AddProductPage (`code` / `desc` / `group` / `unit` / `brand`) and the legacy
 * SQL/.NET schema imported from the previous system (`partno` / `alias` /
 * `groupname` / `unitname` / `brandname` — and several variants). Read both so
 * legacy items don't render as blank rows.
 */
function viewCode(it) {
  return String(
    it.code ?? it.partno ?? it["part no"] ?? it.name ?? it["item name"] ?? it.productid ?? it.productId ?? ""
  ).trim();
}
function viewDesc(it) {
  return String(
    it.desc ?? it.description ?? it.alias ?? it.itemname ?? it["item name"] ?? ""
  ).trim();
}
/** Pass `groupLookup` from `groups` collection so `items.groupid` resolves to a name. */
function viewGroup(it, groupLookup) {
  const direct = String(it.group ?? it.gname ?? it.groupname ?? it.groupName ?? "").trim();
  if (direct) return direct;
  const legacyKey = it.groupid ?? it.groupId;
  if (legacyKey == null || legacyKey === "") return "";
  const resolved = groupLookup?.get(String(legacyKey));
  return resolved != null ? resolved : "";
}
/** Logged once per browser session so a failing unit lookup shows up exactly
 *  one time in the console with the candidate keys we tried — without
 *  spamming for every row of a large catalogue. */
const _unitMissLogged = new Set();
function viewUnit(it, unitLookup) {
  const direct = String(it.unit ?? it.unitname ?? it.unitName ?? "").trim();
  if (direct) return direct;
  const candidates = [it.unitnameid, it.unitId, it.unitid, it.unitcode, it.smallunitid];
  for (const c of candidates) {
    if (c == null || c === "") continue;
    const resolved = unitLookup?.get(String(c));
    if (resolved) return resolved;
  }
  // Diagnostic: first time we see this id without a hit, dump it so the
  // user can compare with their `units` doc. Drops itself in production
  // builds where console output is muted.
  const firstId = candidates.find((c) => c != null && c !== "");
  if (firstId != null && !_unitMissLogged.has(String(firstId))) {
    _unitMissLogged.add(String(firstId));
    if (typeof console !== "undefined" && console.warn) {
      console.warn(
        "[FindItemModal] unit lookup MISS",
        { itemCode: it.code ?? it.partno ?? it.name, candidates, lookupSize: unitLookup?.size ?? 0 }
      );
    }
  }
  return "";
}
function viewBrand(it, brandLookup) {
  const direct = String(it.brand ?? it.brandname ?? it.brandName ?? "").trim();
  if (direct) return direct;
  const legacyKey = it.brandid ?? it.brandId;
  if (legacyKey == null || legacyKey === "") return "";
  const resolved = brandLookup?.get(String(legacyKey));
  return resolved != null ? resolved : "";
}

/** Extra text fields often present on legacy / import rows — folded into "All" search. */
function viewExtraSearchBlob(it) {
  const bits = [
    it.barcode,
    it.bcpn,
    it.hsncode,
    it.hsn,
    it.location,
    it.slNo != null && it.slNo !== "" ? String(it.slNo) : "",
    it.productid,
    it.partno,
    it.name,
    it.mrp != null && it.mrp !== "" ? String(it.mrp) : "",
    it.costprice != null && it.costprice !== "" ? String(it.costprice) : "",
  ];
  return bits
    .filter((x) => x != null && String(x).trim() !== "")
    .map((x) => String(x).trim().toLowerCase())
    .join(" ");
}

const SEARCH_SCOPES = [
  { id: "all", label: "All fields" },
  { id: "code", label: "Code" },
  { id: "desc", label: "Description" },
  { id: "group", label: "Group" },
  { id: "brand", label: "Brand" },
  { id: "unit", label: "Unit" },
];

function tokenizeQuery(q) {
  return String(q || "")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
}

/** Pre-normalize one row so filtering does not repeat FK resolution on every keystroke. */
function buildSearchRow(it, groupLookup, brandLookup, unitLookup) {
  const code = viewCode(it).toLowerCase();
  const desc = viewDesc(it).toLowerCase();
  const group = viewGroup(it, groupLookup).toLowerCase();
  const brand = viewBrand(it, brandLookup).toLowerCase();
  const unit = viewUnit(it, unitLookup).toLowerCase();
  const extra = viewExtraSearchBlob(it);
  const hay = [code, desc, group, brand, unit, extra].filter(Boolean).join(" ");
  return { code, desc, group, brand, unit, hay };
}

function rowMatchesTokens(row, tokens, scope) {
  if (!tokens.length) return true;
  if (scope === "all") return tokens.every((t) => row.hay.includes(t));
  const field =
    scope === "code"
      ? row.code
      : scope === "desc"
        ? row.desc
        : scope === "group"
          ? row.group
          : scope === "brand"
            ? row.brand
            : scope === "unit"
              ? row.unit
              : row.hay;
  return tokens.every((t) => field.includes(t));
}

/**
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - onSelect: (item) => void
 * - defaultSearchScope?: 'all' | 'code' | 'desc' | 'group' | 'brand' | 'unit' — applied when dialog opens
 *
 * Features:
 * - fetch items only while the dialog is open (no full-catalogue download on Product page idle)
 * - search box with debounce (~120ms)
 * - scope chips (narrow to one column) + multi-word AND across fields when scope is All
 * - keyboard navigation: ArrowUp / ArrowDown / Home / End / Enter to select / Escape to close
 * - accessibility: role="dialog", aria-modal, focus trap basics
 * - displays "no products found" when appropriate
 */
export default function FindItemModal({ isOpen, onClose, onSelect, defaultSearchScope = "all" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [searchScope, setSearchScope] = useState("all");
  const [highlightIndex, setHighlightIndex] = useState(-1);
  /** Bump to force a new Firestore read (Refresh). */
  const [reloadKey, setReloadKey] = useState(0);

  /** Legacy `items.groupid` / `brandid` / `unitnameid` → display name. */
  const [groupLookup, setGroupLookup] = useState(() => new Map());
  const [brandLookup, setBrandLookup] = useState(() => new Map());
  const [unitLookup, setUnitLookup] = useState(() => new Map());

  /** Master lookups only while Find is open — avoids extra listeners + work on the Product page. */
  useEffect(() => {
    if (!isOpen) return undefined;
    const subs = [
      onSnapshot(
        collection(db, "groups"),
        (snap) => {
          const recs = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
          setGroupLookup(buildLookup(recs, ["gname", "name"], ["groupid", "groupId"]));
        },
        (err) => console.error("FindItemModal groups snapshot:", err)
      ),
      onSnapshot(
        collection(db, "brands"),
        (snap) => {
          const recs = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
          setBrandLookup(buildLookup(recs, ["brandname", "name"], ["brandid", "brandId"]));
        },
        (err) => console.error("FindItemModal brands snapshot:", err)
      ),
      onSnapshot(
        collection(db, "units"),
        (snap) => {
          const recs = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
          setUnitLookup(
            buildLookup(
              recs,
              ["unitname", "name"],
              ["unitnameid", "unitId", "unitid", "unitcode"],
              { keyAllNumericFields: true }
            )
          );
        },
        (err) => console.error("FindItemModal units snapshot:", err)
      ),
    ];
    return () => {
      for (const u of subs) {
        try {
          u();
        } catch {
          /* ignore */
        }
      }
    };
  }, [isOpen]);

  const dialogRef = useRef(null);
  const searchRef = useRef(null);
  const rowsRef = useRef([]);

  const allowedScopes = useMemo(() => new Set(SEARCH_SCOPES.map((s) => s.id)), []);

  // When the dialog opens: reset search/focus (not when reloadKey changes after refresh)
  useEffect(() => {
    if (!isOpen) return;
    setQuery("");
    setDebouncedQuery("");
    setHighlightIndex(-1);
    const next =
      defaultSearchScope && allowedScopes.has(defaultSearchScope) ? defaultSearchScope : "all";
    setSearchScope(next);
    setTimeout(() => searchRef.current?.focus(), 0);
  }, [isOpen, defaultSearchScope, allowedScopes]);

  /** Drop cached rows when Find closes so the Product page does not hold the full catalogue in RAM. */
  useEffect(() => {
    if (isOpen) return;
    setItems([]);
    setError(null);
    setLoading(false);
  }, [isOpen]);

  /**
   * Items subscription only while the dialog is open — avoids downloading
   * the full catalogue on every Product page visit. Firestore persistent
   * cache still makes re-opens fast after the first load.
   */
  useEffect(() => {
    if (!isOpen) return undefined;
    setLoading(true);
    setError(null);
    const unsub = onSnapshot(
      collection(db, PRODUCT_MASTER_COLLECTION),
      (snap) => {
        const list = snap.docs.map((d) => normalizeProductMasterDoc(d));
        setItems(list);
        setLoading(false);
      },
      (err) => {
        console.error("FindItemModal items snapshot:", err);
        setError("Failed to load products.");
        setLoading(false);
      }
    );
    return () => {
      try {
        unsub();
      } catch {
        /* ignore */
      }
    };
  }, [isOpen, reloadKey]);

  const handleRefreshList = useCallback(() => {
    setReloadKey((k) => k + 1);
  }, []);

  // Debounce query — slightly shorter so the list feels responsive on large catalogs.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query.trim()), 120);
    return () => clearTimeout(id);
  }, [query]);

  const searchTokens = useMemo(() => tokenizeQuery(debouncedQuery), [debouncedQuery]);

  /** One string-normalization pass per item when data or lookups change (not on every keystroke). */
  const searchRowsById = useMemo(() => {
    const m = new Map();
    for (const it of items) {
      if (!it?.id) continue;
      m.set(it.id, buildSearchRow(it, groupLookup, brandLookup, unitLookup));
    }
    return m;
  }, [items, groupLookup, brandLookup, unitLookup]);

  const filteredItems = useMemo(() => {
    if (!searchTokens.length) return items;
    return items.filter((it) => {
      const row = searchRowsById.get(it.id);
      if (!row) return false;
      return rowMatchesTokens(row, searchTokens, searchScope);
    });
  }, [items, searchTokens, searchScope, searchRowsById]);

  /** Only render the first N rows so the table stays fast for huge catalogs. */
  const renderedRows = useMemo(
    () => filteredItems.slice(0, MAX_RENDERED_ROWS),
    [filteredItems]
  );

  // Reset highlighted index when filtered list changes
  useEffect(() => {
    setHighlightIndex(renderedRows.length > 0 ? 0 : -1);
    rowsRef.current = [];
  }, [renderedRows.length, debouncedQuery, searchScope]);

  // Basic focus trap: keep focus inside dialog (Tab handling simplified)
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }

      if (renderedRows.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIndex((prev) => {
          const next = Math.min(prev + 1, renderedRows.length - 1);
          setTimeout(() => rowsRef.current[next]?.scrollIntoView({ block: "nearest" }), 0);
          return next;
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIndex((prev) => {
          const next = Math.max(prev - 1, 0);
          setTimeout(() => rowsRef.current[next]?.scrollIntoView({ block: "nearest" }), 0);
          return next;
        });
      } else if (e.key === "Home") {
        e.preventDefault();
        setHighlightIndex(0);
        setTimeout(() => rowsRef.current[0]?.scrollIntoView({ block: "nearest" }), 0);
      } else if (e.key === "End") {
        e.preventDefault();
        const last = renderedRows.length - 1;
        setHighlightIndex(last);
        setTimeout(() => rowsRef.current[last]?.scrollIntoView({ block: "nearest" }), 0);
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (highlightIndex >= 0 && highlightIndex < renderedRows.length) {
          const item = renderedRows[highlightIndex];
          onSelect(item);
          onClose();
        }
      }
    },
    [renderedRows, highlightIndex, onClose, onSelect]
  );

  // Attach keydown to dialog while open
  useEffect(() => {
    if (!isOpen) return;
    const node = dialogRef.current;
    node?.addEventListener("keydown", handleKeyDown);
    return () => node?.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex justify-center items-center z-50 p-4"
      aria-hidden={false}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Find Product"
        tabIndex={-1}
        className="bg-white text-slate-800 rounded-lg shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col border border-slate-200 focus:outline-none overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-slate-200">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-slate-800">Find Product</h2>
            <span className="text-xs text-slate-500 hidden sm:inline">
              ↑ ↓ Home End · Enter pick · Esc close · words = match all
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleRefreshList}
              disabled={loading}
              className="px-3 py-1.5 text-xs rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              title="Reload product list from server"
            >
              Refresh list
            </button>
            <button
              type="button"
              onClick={() => {
                setQuery("");
                setDebouncedQuery("");
                searchRef.current?.focus();
              }}
              className="px-3 py-1.5 text-xs rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
              title="Clear search"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-xs rounded bg-rose-600 hover:bg-rose-700 text-white shadow-sm"
            >
              Close
            </button>
          </div>
        </div>

        {/* Search bar + scope */}
        <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 space-y-2">
          <input
            ref={searchRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              searchScope === "all"
                ? "Search: code, description, group, brand, unit, barcode, HSN… (use spaces for AND)"
                : `Search in ${SEARCH_SCOPES.find((s) => s.id === searchScope)?.label || "field"} only…`
            }
            className="w-full bg-white border border-slate-300 text-slate-800 placeholder-slate-400 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
            aria-label="Search products"
          />
          <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Search scope">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mr-1">Scope</span>
            {SEARCH_SCOPES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSearchScope(s.id)}
                className={
                  "rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors " +
                  (searchScope === s.id
                    ? "border-indigo-600 bg-indigo-600 text-white shadow-sm"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-100")
                }
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body: table / results */}
        <div className="overflow-auto flex-1 p-0">
          {loading && items.length === 0 ? (
            <div className="p-6 text-center text-slate-500">Loading products…</div>
          ) : error ? (
            <div className="p-6 text-center text-rose-600">{error}</div>
          ) : filteredItems.length === 0 ? (
            <div className="p-6 text-center text-slate-500">
              {items.length === 0
                ? "No products loaded."
                : debouncedQuery
                  ? "No matches — try another word, clear the scope filter, or search a different field."
                  : "No products found."}
            </div>
          ) : (
            <>
              {filteredItems.length > MAX_RENDERED_ROWS ? (
                <div className="px-4 py-2 text-xs text-amber-800 bg-amber-50 border-b border-amber-200">
                  Showing first <strong>{MAX_RENDERED_ROWS}</strong> of{" "}
                  <strong>{filteredItems.length}</strong> matches. Type more in
                  the search box to narrow the list.
                </div>
              ) : null}
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-100 text-slate-600 text-left sticky top-0 z-10">
                    <th className="px-3 py-2 border-b border-slate-200 font-semibold">Code</th>
                    <th className="px-3 py-2 border-b border-slate-200 font-semibold">Description</th>
                    <th className="px-3 py-2 border-b border-slate-200 font-semibold">Group</th>
                    <th className="px-3 py-2 border-b border-slate-200 font-semibold">Unit</th>
                    <th className="px-3 py-2 border-b border-slate-200 font-semibold">Brand</th>
                  </tr>
                </thead>
                <tbody>
                  {renderedRows.map((item, idx) => {
                    const isHighlighted = idx === highlightIndex;
                    return (
                      <tr
                        key={item.id}
                        ref={(el) => {
                          rowsRef.current[idx] = el;
                        }}
                        className={
                          "cursor-pointer transition-colors " +
                          (isHighlighted
                            ? "bg-indigo-50 text-indigo-900"
                            : "hover:bg-slate-50 text-slate-800")
                        }
                        onMouseEnter={() => setHighlightIndex(idx)}
                        onClick={() => {
                          onSelect(item);
                          onClose();
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            onSelect(item);
                            onClose();
                          }
                        }}
                      >
                        <td className="px-3 py-2 border-b border-slate-100">{viewCode(item)}</td>
                        <td className="px-3 py-2 border-b border-slate-100">{viewDesc(item)}</td>
                        <td className="px-3 py-2 border-b border-slate-100">{viewGroup(item, groupLookup)}</td>
                        <td className="px-3 py-2 border-b border-slate-100">{viewUnit(item, unitLookup)}</td>
                        <td className="px-3 py-2 border-b border-slate-100">{viewBrand(item, brandLookup)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-2 bg-slate-50 border-t border-slate-200">
          <span className="text-xs text-slate-500">
            {items.length > 0 ? (
              <>
                <strong className="text-slate-700">{filteredItems.length}</strong> match
                {filteredItems.length === 1 ? "" : "es"} · {items.length} loaded · live
              </>
            ) : (
              ""
            )}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="bg-slate-200 hover:bg-slate-300 text-slate-700 px-4 py-1.5 text-sm rounded"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
